// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Beeline — three modes that PLAY differently (principle #14), not three dials.

import { ALL, type Country, type Pool } from './data';

export interface Mode {
  id: string;
  name: string;
  blurb: string;
  /** Guess budget before the round ends unsolved. */
  guesses: number;
  /** Race timer for a live round (ms). Solo has no clock. */
  timeMs: number;
  /** Show the mystery's outline from turn 0 — the mode's defining difference. */
  showSilhouette: boolean;
  /** Pools the hidden answer can be drawn from. */
  answer: Pool[];
  /** Pools the player may guess / that appear in the search box. */
  guess: Pool[];
}

export const MODES: Record<string, Mode> = {
  silhouette: {
    id: 'silhouette',
    name: 'Silhouette',
    blurb:
      'The hidden country’s shape is shown from the start. Read the outline, then close in — distance, direction and warmth on every guess.',
    guesses: 12,
    timeMs: 90_000,
    showSilhouette: true,
    answer: ['core'],
    guess: ['core', 'extra'],
  },
  blind: {
    id: 'blind',
    name: 'Blind',
    blurb:
      'No outline — just your mental map. Each guess reveals only how far the mystery is and which way it lies. A pure geography read.',
    guesses: 10,
    timeMs: 90_000,
    showSilhouette: false,
    answer: ['core'],
    guess: ['core', 'extra'],
  },
  territories: {
    id: 'territories',
    name: 'Territories',
    blurb:
      'No outline, and the net widens: lesser-known states, dependencies and disputed territories are all in play. For the map obsessives.',
    guesses: 12,
    timeMs: 120_000,
    showSilhouette: false,
    answer: ['core', 'extra', 'territory'],
    guess: ['core', 'extra', 'territory'],
  },
};

export const MODE_IDS = ['silhouette', 'blind', 'territories'] as const;
export const DEFAULT_MODE_ID = 'silhouette';

/**
 * Validate an id off the wire and fall back rather than hand `undefined` to the
 * generator. hasOwn-guarded so a prototype key ('constructor') can't slip a Mode
 * of undefined fields through `MODES[id]`.
 */
export function modeOf(id: string | null | undefined): Mode {
  if (id && Object.hasOwn(MODES, id)) return MODES[id];
  return MODES[DEFAULT_MODE_ID];
}

const poolCache = new Map<string, Country[]>();
function inPools(pools: Pool[]): Country[] {
  const key = pools.slice().sort().join(',');
  let hit = poolCache.get(key);
  if (!hit) {
    const set = new Set(pools);
    hit = ALL.filter((c) => set.has(c.pool));
    poolCache.set(key, hit);
  }
  return hit;
}

/** Countries the hidden answer can be for this mode. */
export function answerPool(mode: Mode): Country[] {
  return inPools(mode.answer);
}

/** Countries that can be guessed / searched for in this mode. */
export function guessPool(mode: Mode): Country[] {
  return inPools(mode.guess);
}

/** Ids that are legal to guess in this mode — used to validate wire input. */
export function guessableIds(mode: Mode): Set<string> {
  return new Set(guessPool(mode).map((c) => c.id));
}
